import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { X, Sparkles, AlertTriangle, Lock } from 'lucide-react';
import { useTrialCountdown } from '@/hooks/useTrialCountdown';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { resolveTrialPlanContext } from '@/lib/trialGating';

const SUBSCRIPTION_PATH = '/profile?tab=billing';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Sticky banner shown above main content while a freemium trial is active.
 * Variants escalate as the trial nears expiry; only `info` is dismissable.
 */
export function TrialBanner() {
  const { data: trial } = useTrialCountdown();
  const { data: billingStatus } = useBillingStatus();
  const { selectedCompany, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const dismissKey = selectedCompany
    ? `trial-banner-dismissed-${selectedCompany.id}-${todayKey()}`
    : null;

  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  // v3.5.2 polish (26/05/26): la fonte di verità per il piano corrente
  // è `useBillingStatus()` — vedi `resolveTrialPlanContext`.
  const { isAgencyOwnerEffective, onPaidPlan } = resolveTrialPlanContext(
    user,
    selectedCompany,
    billingStatus,
  );
  if (isAgencyOwnerEffective || onPaidPlan) return null;

  if (!trial || !trial.active || trial.status !== 'active') return null;
  if (trial.isExpired || trial.daysRemaining < 0) return null;

  const days = trial.daysRemaining;
  let variant: 'info' | 'warning' | 'urgent';
  if (days >= 4) variant = 'info';
  else if (days >= 2) variant = 'warning';
  else variant = 'urgent';

  if (variant === 'info' && dismissed) return null;

  let message = '';
  let cta = '';
  if (variant === 'info') {
    message = t('trial.banner.info', { days });
    cta = t('trial.banner.cta_choose_plan');
  } else if (variant === 'warning') {
    message = t('trial.banner.warning', { days });
    cta = t('trial.banner.cta_upgrade_now');
  } else {
    message = days <= 0 ? t('trial.banner.urgent_today') : t('trial.banner.urgent_tomorrow');
    cta = t('trial.banner.cta_unlock');
  }

  const styles = {
    info: 'bg-blue-50 text-blue-900 border-blue-200',
    warning: 'bg-yellow-50 text-yellow-900 border-yellow-200',
    urgent: 'bg-orange-100 text-orange-900 border-orange-300',
  }[variant];

  const Icon = variant === 'info' ? Sparkles : variant === 'warning' ? AlertTriangle : Lock;

  const handleClick = () => navigate(SUBSCRIPTION_PATH);
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={handleClick}
      className={cn(
        'border-b cursor-pointer transition-colors hover:brightness-95 shrink-0',
        styles
      )}
    >
      <div className="max-w-screen-2xl mx-auto w-full px-4 md:px-6 py-2 flex items-center gap-3">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium flex-1 truncate">{message}</span>
        <button
          onClick={(e) => { e.stopPropagation(); handleClick(); }}
          className={cn(
            'text-xs font-semibold px-3 py-1 rounded-md shrink-0 transition-colors',
            variant === 'urgent'
              ? 'bg-orange-900 text-white hover:bg-orange-950'
              : variant === 'warning'
                ? 'bg-yellow-900 text-white hover:bg-yellow-950'
                : 'bg-blue-900 text-white hover:bg-blue-950'
          )}
        >
          {cta}
        </button>
        {variant === 'info' && (
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="shrink-0 opacity-60 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default TrialBanner;